import React from "react";

import { Box, Stack, Typography } from "@mui/material";

export interface FieldLabelProps {
  label?: React.ReactNode;
  /** Inline content to the right of the label — info icon, "(optional)",
   *  short helper text, etc. */
  adornment?: React.ReactNode;
  /**
   * Content pinned to the RIGHT END of the label row — a per-field "Clear", a
   * count, a small link.
   *
   * Distinct from `adornment`, which sits immediately beside the label and reads
   * as part of it. An action belongs to the field, not to the label's sentence,
   * so it is pushed to the far edge instead. It exists here rather than in each
   * caller because a caller that needs a label row WITH an action would
   * otherwise have to hand-roll the whole row — and with it the label→control
   * gap this component exists to own (working-conventions §31). That is exactly
   * what the filter drawer's `FilterField` did before it was brought onto this.
   */
  action?: React.ReactNode;
  /** Typography variant for the label text. Defaults to the field standard. */
  variant?: "body1" | "body2" | "body3" | "body4";
}

/**
 * The label→control gap, keyed to the label's own size.
 *
 * Still ONE owner (working-conventions §31) — a caller picks a variant, not a
 * number, and can never fake the gap with a margin on its control. But a fixed
 * 8px is set against `body3`, the form-field default; the larger `body1`/`body2`
 * labels (the filter drawer's field titles) read cramped at the same value,
 * because the perceived gap is what is left after the type's own leading, and
 * bigger type eats more of it. So the gap steps up with the type rather than
 * every larger label hand-rolling its own spacing — which is exactly what the
 * filter fields were doing before they were brought onto this component.
 *
 * Deliberately keyed to `variant` and not exposed as a free `gap` prop: two
 * fields with the same label size must never be able to disagree.
 */
/*
 * ABSOLUTE px, not theme multipliers (working-conventions §47). These used to be
 * `3`/`2` with the comments below asserting 12px and 8px, and that was true in
 * exactly one of the two apps: `@repo/ui` sets a 4px base, `apps/app` never
 * overrides MUI's default 8px, so every one of these rendered DOUBLE there. The
 * form-field standard §31 calls 8px was measured at 16px in the consumer app.
 * A layer-1 component cannot "convert per app" at its call site, so it must not
 * use a multiplier at all.
 */
const LABEL_GAP: Record<NonNullable<FieldLabelProps["variant"]>, string> = {
  body1: "12px",
  body2: "12px",
  body3: "8px", // the form-field standard
  body4: "8px",
};

/**
 * Canonical field label + the standard label→control gap.
 *
 * Single source of truth for field labels across the app: the shared `Input`
 * uses it internally, and the platform field wrappers (TextField, SelectField,
 * DatePickerField, RichTextEditorField) consume it so every field has identical
 * label typography and spacing regardless of the control beneath it. Change the
 * gap HERE to change it everywhere.
 */
export const FieldLabel = ({ label, adornment, action, variant = "body3" }: FieldLabelProps) => {
  if (!label && !adornment && !action) return null;

  // A STRING adornment has to be wrapped before it goes into the Stack.
  // MUI's `spacing` is implemented as `& > :not(style) ~ :not(style) { margin }`,
  // which matches ELEMENTS — a bare text node is not one, so it receives no
  // margin and renders flush against the label: `Attachment(optional)`. This
  // prop's own docblock offers "(optional)" as an example value, so passing a
  // string is the expected usage and it silently produced that. Wrapping also
  // gets the hint the muted styling it should have had; every existing caller
  // passes an element and is unaffected.
  // Styled via `sx` rather than <Typography variant="body4">: packages/ui compiles
  // without the app's MUI Typography module augmentation, so `body3`/`body4` are
  // not assignable here — that is a pre-existing gap this file should not widen.
  //
  // The adornment must also never drive the row's HEIGHT. `alignItems: center`
  // means any child taller than the label grows the row — and since the row
  // carries the label→control margin, the control below it moves down too. So a
  // field WITH an adornment sits lower than the field beside it: measured at
  // 1.9px for `(optional)`, whose FormHelperText line box is 19.92px against the
  // label's 18px. Small in isolation, but two fields sharing a row read as
  // misaligned immediately, and it was reported as exactly that.
  //
  // Pinned to the label's own line box, read from the same theme entry the label
  // Typography uses, with descendants forced to inherit so a caller-supplied
  // element cannot opt out (MUI sets its own line-height on FormHelperText,
  // Typography and Chip alike — that is the whole cause here). Only the LINE BOX
  // is constrained: font size, colour and the glyphs are untouched.
  const renderedAdornment = !adornment ? null : (
    <Box
      component="span"
      sx={{
        display: "flex",
        alignItems: "center",
        lineHeight: (theme) =>
          (theme.typography as Record<string, { lineHeight?: number | string }>)[variant]
            ?.lineHeight ?? 1.5,
        "& *": { lineHeight: "inherit" },
        ...(typeof adornment === "string" || typeof adornment === "number"
          ? { fontSize: 12, color: "text.secondary" }
          : null),
      }}
    >
      {adornment}
    </Box>
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      // Only spread when there is something to push away from the label; without
      // an action the row stays a plain left-aligned label + adornment.
      justifyContent={action ? "space-between" : undefined}
      sx={{ mb: LABEL_GAP[variant] }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        {label && (
          <Typography variant={variant} sx={{ display: "block" }}>
            {label}
          </Typography>
        )}
        {renderedAdornment}
      </Stack>
      {action}
    </Stack>
  );
};
